import React, { useMemo } from "react";
import { Flex, LoadingIndicator, Text } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { type Sandbox, MAX_RECENT_SANDBOXES } from "../consts";
import { useRecentSandboxKeys } from "../hooks";
import { SandboxListbox } from "../SandboxListbox";
import * as widgetStyles from "../styles_vanilla.css";
import * as styles from "./styles_vanilla.css";

export type RecentSandboxesProps = {
  sandboxes: ReadonlyArray<Sandbox> | undefined;
  currentRoutingKey: string | undefined;
  sandboxesLoading: boolean;
  onSwitch: (routingKey: string) => void;
};

export const RecentSandboxes: React.FC<RecentSandboxesProps> = ({
  sandboxes,
  currentRoutingKey,
  sandboxesLoading,
  onSwitch,
}) => {
  const recentKeys = useRecentSandboxKeys();

  const recentSandboxes = useMemo(
    () =>
      recentKeys
        .map((key) => sandboxes?.find((sb) => sb.routingKey === key))
        .filter((sb): sb is Sandbox => sb !== undefined)
        .filter((sb) => sb.routingKey !== currentRoutingKey)
        .slice(0, MAX_RECENT_SANDBOXES),
    [recentKeys, sandboxes, currentRoutingKey],
  );

  if (recentKeys.length === 0) return null;
  if (!sandboxesLoading && recentSandboxes.length === 0) return null;

  return (
    <div className={styles.recentSectionStyle}>
      <Text variant="upper-sm" className={widgetStyles.sectionLabelStyle}>
        Recent sandboxes
      </Text>
      {sandboxesLoading ? (
        <Flex
          justifyContent="center"
          alignItems="center"
          style={{ padding: Sizes.MD }}
          role="status"
          aria-label="Loading sandboxes"
        >
          <LoadingIndicator size="small" />
        </Flex>
      ) : (
        <SandboxListbox
          groups={[{ sandboxes: recentSandboxes }]}
          ariaLabel="Recent sandboxes"
          showStatusIcon
          onSwitch={onSwitch}
        />
      )}
    </div>
  );
};
